import React, { useState, useMemo, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  IconButton, 
  Collapse, 
  Chip,
  Avatar,
  Tooltip,
  useMediaQuery,
  Stack,
  Paper,
  Fade
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Close,
  ExpandMore,
  AttachFile,
  Assignment,
  Refresh,
  Info,
  CalendarToday,
  People,
  FolderOpen,
  TaskAlt,
  Gavel
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import API from '../../API';
import BlitzChatRulesSection from './blitzrules';

/* ------------------------------------------------------------------ */
/* Helper Functions                                                  */
/* ------------------------------------------------------------------ */
function getStatusColor(status) {
  switch (status?.toLowerCase()) {
    case 'completed':
    case 'paid':
    case 'accepted':
      return 'success';
    case 'pending':
    case 'sent':
      return 'warning';
    case 'rejected':
    case 'cancelled':
      return 'error';
    default:
      return 'default';
  }
}

function formatDate(dateString) {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

function formatFileSize(bytes) {
  if (!bytes) return 'Unknown';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

/* ------------------------------------------------------------------ */
/* ConversationDetailsPanel Component                                */
/* ------------------------------------------------------------------ */
export default function ConversationDetailsPanel({ conversationId, onClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // State management
  const [expandedSections, setExpandedSections] = useState({
    info: true,
    attachments: false,
    deliverables: false,
    rules: false,
  });

  // Toggle section expansion
  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);

  /* ------------------------------------------------------------------ */
  /* Data Fetching                                                    */
  /* ------------------------------------------------------------------ */
  const {
    data: metadata,
    isLoading: metadataLoading,
    error: metadataError,
    refetch: refetchMetadata
  } = useQuery(
    ['conversation', 'metadata', conversationId],
    () => API.conversations.getMetadata(conversationId),
    { 
      enabled: !!conversationId,
      staleTime: 1000 * 60 * 5, 
    }
  );

  const {
    data: conversationData,
    isLoading: messagesLoading,
    error: messagesError,
    refetch: refetchMessages
  } = useQuery(
    ['messages', conversationId],
    () => API.conversations.getMessages(conversationId),
    { 
      enabled: !!conversationId,
      staleTime: 1000 * 60 * 2, 
    }
  );

  /* ------------------------------------------------------------------ */
  /* Processed Data                                                   */
  /* ------------------------------------------------------------------ */
  const conversationInfo = useMemo(() => {
    const conv = metadata?.conversation || conversationData?.conversation || {};
    return {
      id: conversationId,
      displayName: conv.display_name || 'Unnamed Conversation',
      createdAt: conv.created_at,
      updatedAt: conv.updated_at,
      participantCount: conversationData?.participants?.length || metadata?.participants?.length || 0,
      campaignName: conv.campaign_name,
      partnershipName: conv.partnership_name,
      theme: conv.theme,
    };
  }, [metadata, conversationData, conversationId]);

  const allAttachments = useMemo(() => {
    if (!conversationData?.messages) return [];
    const attachments = [];
    conversationData.messages.forEach((msg) => {
      if (msg.attachments) {
        attachments.push({
          ...msg.attachments,
          messageId: msg.id,
          timestamp: msg.created_at,
          senderName: msg.sender?.username || 'Unknown',
        });
      }
    });
    return attachments.slice(0, 20);
  }, [conversationData?.messages]);

  const deliverables = useMemo(() => {
    if (!conversationData?.messages) return [];
    return conversationData.messages
      .filter(msg => !!msg.deliverable)
      .map(msg => ({
        ...msg.deliverable,
        messageId: msg.id,
        timestamp: msg.created_at,
        senderName: msg.sender?.username || 'Unknown',
      }))
      .slice(0, 10);
  }, [conversationData?.messages]);

  /* ------------------------------------------------------------------ */
  /* Render Components                                                */
  /* ------------------------------------------------------------------ */
  const renderSectionHeader = (title, count, icon, sectionKey) => {
    const isExpanded = expandedSections[sectionKey];
    return (
      <Box
        onClick={() => toggleSection(sectionKey)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          py: 2,
          px: 3,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          transition: 'all 0.2s ease',
          '&:hover': { 
            bgcolor: alpha(theme.palette.action.hover, 0.4),
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              transition: 'all 0.2s ease',
            }}
          >
            {React.cloneElement(icon, { sx: { fontSize: 20 } })}
          </Box>
          <Typography variant="body1" fontWeight="600" color="text.primary">
            {title}
          </Typography>
          {count !== undefined && count > 0 && (
            <Chip 
              label={count} 
              size="small" 
              sx={{ 
                height: 22, 
                minWidth: 22,
                fontSize: '0.75rem',
                fontWeight: 700,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: 'primary.main',
                borderRadius: 1.5,
              }} 
            />
          )}
        </Box>
        <Box 
          sx={{ 
            color: 'text.secondary',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
        >
          <ExpandMore sx={{ fontSize: 22 }} />
        </Box>
      </Box>
    );
  };

  const safeId = String(conversationId || '');

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        boxSizing: 'border-box',
      }}
    >
      {/* Sticky Header */}
      <Box
        sx={{
          py: 2.5,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: alpha(theme.palette.background.paper, 0.95),
          backdropFilter: 'blur(12px)',
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight="700" lineHeight={1.2} color="text.primary">
            Conversation Details
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', mt: 0.25, display: 'block' }}>
            ID: {safeId.slice(0, 8)}{safeId.length > 8 ? '...' : ''}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton 
              onClick={(e) => { e.stopPropagation(); refetchMetadata(); refetchMessages(); }}
              size="small"
              sx={{ 
                borderRadius: 2,
                '&:hover': {
                  bgcolor: alpha(theme.palette.action.hover, 0.6),
                },
              }}
            >
              <Refresh sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton 
              onClick={onClose} 
              size="small"
              sx={{ 
                borderRadius: 2,
                '&:hover': {
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  color: 'error.main',
                },
              }}
            >
              <Close sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Scrollable Content Area */}
      <Box 
        sx={{ 
          flex: 1, 
          overflowY: 'auto', 
          overflowX: 'hidden',
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-track': { 
            background: alpha(theme.palette.action.hover, 0.2),
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': { 
            backgroundColor: alpha(theme.palette.action.active, 0.2), 
            borderRadius: '4px',
            '&:hover': { 
              backgroundColor: alpha(theme.palette.action.active, 0.3),
            },
          },
        }}
      >
        {/* Loading State */}
        {metadataLoading && (
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[1, 2, 3].map((i) => (
              <Paper 
                key={i} 
                elevation={0}
                sx={{ 
                  p: 2.5, 
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                  bgcolor: alpha(theme.palette.action.hover, 0.3),
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ width: 44, height: 44, borderRadius: 2.5, bgcolor: alpha(theme.palette.action.active, 0.1) }} />
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ width: '40%', height: 18, borderRadius: 1.5, bgcolor: alpha(theme.palette.action.active, 0.1), mb: 1 }} />
                    <Box sx={{ width: '60%', height: 14, borderRadius: 1.5, bgcolor: alpha(theme.palette.action.active, 0.08) }} />
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        )}

        {(metadataError || messagesError) && (
          <Box sx={{ p: 3 }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                bgcolor: alpha(theme.palette.error.main, 0.05),
              }}
            >
              <Typography variant="body1" color="error" fontWeight="600" align="center">
                Could not load conversation details.
              </Typography>
            </Paper>
          </Box>
        )}

        {/* Conversation Info Section */}
        {!metadataLoading && (
          <>
            {renderSectionHeader('General Information', undefined, <Info />, 'info')}
            <Collapse in={expandedSections.info}>
              <Box sx={{ p: 3, pt: 2.5 }}>
                {/* Professional Info Card */}
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 3, 
                    borderRadius: 3,
                    bgcolor: 'background.paper',
                    border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                    boxShadow: 'none',
                  }}
                >
                  {/* Conversation Name */}
                  <Box sx={{ mb: 3 }}>
                    <Typography 
                      variant="caption" 
                      color="text.secondary" 
                      fontWeight={600} 
                      sx={{ 
                        textTransform: 'uppercase', 
                        letterSpacing: 0.8,
                        fontSize: '0.7rem',
                        display: 'block',
                        mb: 0.75,
                      }}
                    >
                      Conversation Name
                    </Typography>
                    <Typography 
                      variant="h6" 
                      fontWeight="700" 
                      color="text.primary"
                      sx={{ 
                        fontSize: '1.1rem',
                        lineHeight: 1.3,
                      }}
                    >
                      {conversationInfo.displayName}
                    </Typography>
                  </Box>

                  {/* Associated Campaign/Partnership */}
                  {(conversationInfo.campaignName || conversationInfo.partnershipName) && (
                    <Box sx={{ mb: 2.5 }}>
                      <Chip
                        label={conversationInfo.campaignName || conversationInfo.partnershipName}
                        color={conversationInfo.campaignName ? 'primary' : 'secondary'}
                        size="small"
                        sx={{ 
                          fontWeight: 600,
                          borderRadius: 1.5,
                          height: 28,
                          fontSize: '0.8rem',
                          px: 0.5,
                        }}
                      />
                    </Box>
                  )}

                  {/* Divider */}
                  <Box sx={{ 
                    height: 1, 
                    bgcolor: alpha(theme.palette.divider, 0.6), 
                    my: 2.5 
                  }} />

                  {/* Stats Grid - Professional Layout */}
                  <Box 
                    sx={{ 
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                      gap: 2.5,
                    }}
                  >
                    {/* Participants */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 2.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: 'primary.main',
                          flexShrink: 0,
                        }}
                      >
                        <People sx={{ fontSize: 20 }} />
                      </Box>
                      <Box>
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          fontWeight={500}
                          sx={{ 
                            fontSize: '0.7rem', 
                            display: 'block', 
                            lineHeight: 1.2,
                            mb: 0.25,
                          }}
                        >
                          Participants
                        </Typography>
                        <Typography variant="body1" fontWeight="700" sx={{ lineHeight: 1.2 }}>
                          {conversationInfo.participantCount}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Created Date */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 2.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: alpha(theme.palette.success.main, 0.1),
                          color: 'success.main',
                          flexShrink: 0,
                        }}
                      >
                        <CalendarToday sx={{ fontSize: 20 }} />
                      </Box>
                      <Box>
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          fontWeight={500}
                          sx={{ 
                            fontSize: '0.7rem', 
                            display: 'block', 
                            lineHeight: 1.2,
                            mb: 0.25,
                          }}
                        >
                          Created
                        </Typography>
                        <Typography variant="body1" fontWeight="700" sx={{ lineHeight: 1.2 }}>
                          {formatDate(conversationInfo.createdAt)}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Last Activity */}
                    {conversationInfo.updatedAt && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 2.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: alpha(theme.palette.info.main, 0.1),
                            color: 'info.main',
                            flexShrink: 0,
                          }}
                        >
                          <Refresh sx={{ fontSize: 20 }} />
                        </Box>
                        <Box>
                          <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            fontWeight={500}
                            sx={{ 
                              fontSize: '0.7rem', 
                              display: 'block', 
                              lineHeight: 1.2,
                              mb: 0.25,
                            }}
                          >
                            Last Activity
                          </Typography>
                          <Typography variant="body1" fontWeight="700" sx={{ lineHeight: 1.2 }}>
                            {formatDate(conversationInfo.updatedAt)}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Box>
            </Collapse>

            {/* Attachments Section */}
            {renderSectionHeader('Attachments', allAttachments.length, <AttachFile />, 'attachments')}
            <Collapse in={expandedSections.attachments}>
              <Box sx={{ p: 3, pt: 2.5 }}>
                {messagesLoading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress size={28} thickness={4} />
                  </Box>
                ) : allAttachments.length === 0 ? (
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 4, 
                      textAlign: 'center',
                      bgcolor: alpha(theme.palette.background.default, 0.4),
                      borderRadius: 3,
                      border: `1px dashed ${alpha(theme.palette.divider, 0.7)}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 3,
                        bgcolor: alpha(theme.palette.action.active, 0.08),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                      }}
                    >
                      <FolderOpen sx={{ fontSize: 28, color: 'text.disabled' }} />
                    </Box>
                    <Typography variant="body1" fontWeight="600" color="text.secondary" gutterBottom>
                      No files shared yet
                    </Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.75rem' }}>
                      Attachments will appear here when shared
                    </Typography>
                  </Paper>
                ) : (
                  <Stack spacing={1.5}>
                    {allAttachments.map((att, i) => (
                      <Fade in key={i} timeout={300} style={{ transitionDelay: `${i * 50}ms` }}>
                        <Paper 
                          elevation={0}
                          sx={{ 
                            p: 2.5,
                            borderRadius: 2.5,
                            border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                            bgcolor: 'background.paper',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: 'primary.main',
                              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
                              transform: 'translateY(-1px)',
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar 
                              variant="rounded" 
                              sx={{ 
                                width: 44, 
                                height: 44, 
                                borderRadius: 2.5,
                                bgcolor: alpha(theme.palette.primary.main, 0.1), 
                                color: 'primary.main',
                              }}
                            >
                              <AttachFile sx={{ fontSize: 22 }} />
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body1" fontWeight="600" noWrap sx={{ fontSize: '0.95rem', mb: 0.25 }}>
                                {att.filename || 'Untitled File'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                                Shared by {att.senderName}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Chip 
                                  label={formatDate(att.timestamp)} 
                                  size="small" 
                                  variant="outlined"
                                  sx={{ 
                                    height: 20, 
                                    fontSize: '0.7rem',
                                    fontWeight: 500,
                                    borderRadius: 1.5,
                                  }}
                                />
                                {att.size && (
                                  <Chip 
                                    label={formatFileSize(att.size)} 
                                    size="small" 
                                    variant="outlined"
                                    sx={{ 
                                      height: 20, 
                                      fontSize: '0.7rem',
                                      fontWeight: 500,
                                      borderRadius: 1.5,
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </Paper>
                      </Fade>
                    ))}
                  </Stack>
                )}
              </Box>
            </Collapse>

            {/* Deliverables Section */}
            {renderSectionHeader('Deliverables', deliverables.length, <Assignment />, 'deliverables')}
            <Collapse in={expandedSections.deliverables}>
              <Box sx={{ p: 3, pt: 2.5 }}>
                {messagesLoading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress size={28} thickness={4} />
                  </Box>
                ) : deliverables.length === 0 ? (
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 4, 
                      textAlign: 'center',
                      bgcolor: alpha(theme.palette.background.default, 0.4),
                      borderRadius: 3,
                      border: `1px dashed ${alpha(theme.palette.divider, 0.7)}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 3,
                        bgcolor: alpha(theme.palette.action.active, 0.08),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                      }}
                    >
                      <TaskAlt sx={{ fontSize: 28, color: 'text.disabled' }} />
                    </Box>
                    <Typography variant="body1" fontWeight="600" color="text.secondary" gutterBottom>
                      No deliverables yet
                    </Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.75rem' }}>
                      Deliverables will appear here when created
                    </Typography>
                  </Paper>
                ) : (
                  <Stack spacing={1.5}>
                    {deliverables.map((del, i) => (
                      <Fade in key={i} timeout={300} style={{ transitionDelay: `${i * 50}ms` }}>
                        <Paper 
                          elevation={0}
                          sx={{ 
                            p: 2.5,
                            borderRadius: 2.5,
                            border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                            bgcolor: 'background.paper',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: 'primary.main',
                              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
                              transform: 'translateY(-1px)',
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body1" fontWeight="600" gutterBottom sx={{ fontSize: '0.95rem', mb: 0.25 }}>
                                {del.name || 'Untitled Deliverable'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.75rem' }}>
                                By {del.senderName}
                              </Typography>
                            </Box>
                            <Chip 
                              label={del.status} 
                              size="small" 
                              color={getStatusColor(del.status)} 
                              sx={{ 
                                height: 24, 
                                fontSize: '0.7rem', 
                                fontWeight: 600,
                                borderRadius: 1.5,
                                textTransform: 'capitalize',
                                px: 1,
                              }}
                            />
                          </Box>
                          {del.description && (
                            <Typography variant="body2" color="text.secondary" display="block" mb={1.5} sx={{ fontSize: '0.8rem', lineHeight: 1.5 }}>
                              {del.description}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip 
                              label={formatDate(del.timestamp)} 
                              size="small" 
                              variant="outlined"
                              sx={{ 
                                height: 20, 
                                fontSize: '0.7rem',
                                fontWeight: 500,
                                borderRadius: 1.5,
                              }}
                            />
                            {del.due_date && (
                              <Chip 
                                label={`Due ${formatDate(del.due_date)}`}
                                size="small" 
                                variant="outlined"
                                color="warning"
                                sx={{ 
                                  height: 20, 
                                  fontSize: '0.7rem',
                                  fontWeight: 500,
                                  borderRadius: 1.5,
                                }}
                              />
                            )}
                          </Box>
                        </Paper>
                      </Fade>
                    ))}
                  </Stack>
                )}
              </Box>
            </Collapse>

            {/* Blitz Chat Rules Section */}
            {renderSectionHeader('Chat Guidelines', undefined, <Gavel />, 'rules')}
            <Collapse in={expandedSections.rules}>
              <Box sx={{ p: 3, pt: 2.5 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 0,
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                    overflow: 'hidden',
                  }}
                >
                  <BlitzChatRulesSection />
                </Paper>
              </Box>
            </Collapse>
          </>
        )}
      </Box>
    </Box>
  );
}
